import React, { useMemo, useState } from "react";
import {
    useApiUrl,
    useCustom,
    useGetIdentity,
    useTranslate,
} from "@refinedev/core";
import {
    Card,
    Col,
    Empty,
    Flex,
    Input,
    Radio,
    Row,
    Spin,
    Typography,
    theme,
} from "antd";
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    SearchOutlined,
    TagsOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { usePageStyles } from "../../shared/styles/usePageStyles";
import { IProduct, ProductType } from "../../models/product";
import { useCustomer } from "../../hooks/auth";

interface IProductWithPrice extends IProduct {
    price: number;
}

export const TarifsList: React.FC = () => {
    const _t = useTranslate();
    const apiUrl = useApiUrl();
    const pageStyles = usePageStyles();
    const { token: antdToken } = theme.useToken();
    const isCustomer = useCustomer();
    const { data: user } = useGetIdentity<{ account?: { id?: number } }>();
    const { id: accountIdFromParams } = useParams<{ id: string }>();
    const accountId = accountIdFromParams
        ? Number(accountIdFromParams)
        : user?.account?.id;

    const [search, setSearch] = useState("");
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    // Keep full query result: useCustom().data is HttpResponse, products at .data.data
    // (same shape as order create). Do not destructure `data` then read `.data.data`.
    const productQueryResult = useCustom<IProductWithPrice[]>({
        url: `${apiUrl}/products/prices`,
        method: "get",
        config: {
            query: accountId ? { accountId } : {},
        },
        queryOptions: {
            // Customers: backend resolves account from JWT even without accountId
            enabled: !!accountId || isCustomer,
        },
    });

    const products: IProductWithPrice[] = useMemo(() => {
        const data = productQueryResult.data?.data;
        return Array.isArray(data) ? data : [];
    }, [productQueryResult.data]);

    const isLoading = productQueryResult.isLoading;

    const bases = useMemo(
        () =>
            products.filter(
                (p) => (p.type || "").toLowerCase() === ProductType.BASE,
            ),
        [products],
    );

    const productGroups = useMemo(() => {
        const groups = new Set<string>();
        products.forEach((p) => {
            groups.add((p.group || "").toString());
        });
        return Array.from(groups).sort((a, b) => a.localeCompare(b));
    }, [products]);

    const optionsByGroup = useMemo(() => {
        const options = products.filter(
            (p) => (p.type || "").toLowerCase() === ProductType.OPTION,
        );
        const byGroup: Record<string, IProductWithPrice[]> = {};
        options.forEach((p) => {
            const g = p.group || "";
            if (!byGroup[g]) byGroup[g] = [];
            byGroup[g].push(p);
        });
        return Object.entries(byGroup).sort(([a], [b]) => a.localeCompare(b));
    }, [products]);

    const matchesFilters = (p: IProductWithPrice): boolean => {
        const q = search.trim().toLowerCase();
        const matchesSearch = !q || (p.name || "").toLowerCase().includes(q);
        const matchesGroup =
            selectedGroup == null || (p.group || "").toString() === selectedGroup;
        return matchesSearch && matchesGroup;
    };

    const filteredBases = useMemo(
        () => bases.filter(matchesFilters),
        [bases, search, selectedGroup],
    );

    const filteredOptionsByGroup = useMemo(
        () =>
            optionsByGroup
                .map(([group, opts]) => [group, opts.filter(matchesFilters)] as const)
                .filter(([, opts]) => opts.length > 0),
        [optionsByGroup, search, selectedGroup],
    );

    const getProductPrice = (productId: number): number => {
        const product = products.find((p) => p.id === productId);
        return product?.price ?? 0;
    };

    const toggleSelected = (productId: number): void => {
        setSelectedIds((prev) =>
            prev.includes(productId)
                ? prev.filter((id) => id !== productId)
                : [...prev, productId],
        );
    };

    const totalCredits = useMemo(
        () => selectedIds.reduce((sum, id) => sum + getProductPrice(id), 0),
        [selectedIds, products],
    );

    const selectedBreakdown = useMemo(() => {
        if (selectedIds.length === 0) {
            return _t("tarifs.noSelection");
        }
        return selectedIds
            .map((id) => {
                const product = products.find((p) => p.id === id);
                if (!product) return null;
                const price = getProductPrice(id);
                const priceLabel =
                    price === 0
                        ? _t("orders.stepped.creditsFree")
                        : String(price);
                return `${product.name} (${priceLabel})`;
            })
            .filter(Boolean)
            .join(" + ");
    }, [selectedIds, products, _t]);

    const formatPrice = (price: number): string => {
        if (price === 0) return _t("orders.stepped.creditsFree");
        return `${price} ${_t("tarifs.creditsAbbr")}`;
    };

    const selectedCardStyle = {
        ...pageStyles.selectableCard,
        borderColor: antdToken.colorPrimary,
        backgroundColor: antdToken.colorPrimaryBg,
        boxShadow: `0 0 0 2px ${antdToken.colorPrimaryBorder}`,
    };

    const renderProductCard = (p: IProductWithPrice) => {
        const isSelected = selectedIds.includes(p.id);
        const price = getProductPrice(p.id);
        return (
            <Card
                key={p.id}
                size="small"
                className="cursor-pointer w-full min-w-0 transition-colors !rounded-xl"
                styles={{ body: { padding: "10px 12px" } }}
                style={isSelected ? selectedCardStyle : pageStyles.selectableCard}
                onClick={() => toggleSelected(p.id)}
            >
                <Flex justify="space-between" align="center" gap={8}>
                    <Flex align="center" gap={8} className="min-w-0 flex-1">
                        {isSelected ? (
                            <CheckCircleFilled
                                className="text-base shrink-0"
                                style={{ color: antdToken.colorPrimary }}
                            />
                        ) : (
                            <CheckCircleOutlined
                                className="text-base shrink-0"
                                style={{ color: antdToken.colorBorder }}
                            />
                        )}
                        <Typography.Text strong className="text-sm line-clamp-2 min-w-0">
                            {p.name}
                        </Typography.Text>
                    </Flex>
                    <Typography.Text
                        className={`text-sm font-semibold whitespace-nowrap shrink-0 ${price === 0 ? "text-emerald-500" : ""}`}
                        style={price === 0 ? undefined : { color: antdToken.colorPrimary }}
                    >
                        {formatPrice(price)}
                    </Typography.Text>
                </Flex>
            </Card>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto flex flex-col h-[calc(100vh-112px)] min-h-[480px]">
            <Flex align="center" gap={10} className="mb-5 shrink-0">
                <TagsOutlined style={{ color: antdToken.colorPrimary, fontSize: 22 }} />
                <Typography.Title level={3} className="!m-0">
                    {_t("tarifs.titles.list")}
                </Typography.Title>
            </Flex>

            <Flex wrap="wrap" gap={12} align="center" className="mb-5 shrink-0">
                <Input
                    prefix={<SearchOutlined />}
                    placeholder={_t("tarifs.searchPlaceholder")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                    className="max-w-sm"
                />
                {productGroups.length > 0 && (
                    <Radio.Group
                        value={selectedGroup ?? "__all__"}
                        onChange={(e) => {
                            const value = e.target.value as string;
                            setSelectedGroup(value === "__all__" ? null : value);
                        }}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <Radio.Button value="__all__">
                            {_t("tarifs.allGroups")}
                        </Radio.Button>
                        {productGroups.map((group) => (
                            <Radio.Button key={group || "ungrouped"} value={group}>
                                {group || _t("orders.stepped.group.ungrouped")}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                )}
            </Flex>

            <div className="flex-1 min-h-0 overflow-y-auto">
                <Spin spinning={isLoading}>
                    {!isLoading && products.length === 0 ? (
                        <Empty description={_t("tarifs.empty")} />
                    ) : (
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <Card className="shadow-lg" style={pageStyles.stepperCard}>
                                    <Typography.Title level={4} className="mb-1">
                                        {_t("tarifs.bases")}
                                    </Typography.Title>
                                    <Typography.Text type="secondary" className="block mb-4">
                                        {_t("tarifs.basesSubtitle")}
                                    </Typography.Text>
                                    {filteredBases.length === 0 ? (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={_t("tarifs.empty")}
                                        />
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {filteredBases.map(renderProductCard)}
                                        </div>
                                    )}
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Card className="shadow-lg" style={pageStyles.stepperCard}>
                                    <Typography.Title level={4} className="mb-1">
                                        {_t("tarifs.options")}
                                    </Typography.Title>
                                    <Typography.Text type="secondary" className="block mb-4">
                                        {_t("tarifs.optionsSubtitle")}
                                    </Typography.Text>
                                    {filteredOptionsByGroup.length === 0 ? (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={_t("tarifs.empty")}
                                        />
                                    ) : (
                                        <div className="space-y-5">
                                            {filteredOptionsByGroup.map(([groupName, opts]) => (
                                                <div key={groupName || "ungrouped"}>
                                                    <Typography.Text
                                                        strong
                                                        className="uppercase text-xs tracking-wider text-gray-600 dark:text-gray-400 block mb-2"
                                                    >
                                                        {groupName || _t("tarifs.options")}
                                                    </Typography.Text>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                        {opts.map(renderProductCard)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Spin>
            </div>

            <Card
                className="shadow-lg shrink-0 mt-4 border border-solid"
                style={{
                    ...pageStyles.stepperCard,
                    borderColor: antdToken.colorBorderSecondary,
                }}
                styles={{ body: { padding: "12px 20px" } }}
            >
                <Flex align="center" justify="space-between" gap={12} wrap="wrap">
                    <div className="min-w-0 flex-1">
                        <Typography.Text type="secondary" className="text-xs uppercase tracking-wide">
                            {_t("tarifs.total")}
                        </Typography.Text>
                        <Typography.Text type="secondary" className="text-sm block truncate">
                            {selectedBreakdown}
                        </Typography.Text>
                    </div>
                    <Typography.Title level={3} className="!m-0 whitespace-nowrap">
                        {totalCredits}{" "}
                        <span className="text-sm font-normal text-gray-500">
                            {_t("tarifs.creditsAbbr")}
                        </span>
                    </Typography.Title>
                </Flex>
            </Card>
        </div>
    );
};
